import React, { useState } from 'react';
import { toast } from 'react-toastify';
import TraderAutoStakingTab from './TraderAutoStakingTab';
import StakeInfoForm from './StakeInfoForm';
import { apiServices } from '../../utils/apiServices';
import { useAppContext } from '../../context/AppContext';
import { SN_VAL_ADDRS } from '../../utils/const';
import { GradientBorderBox } from '../GradientBorderBox';
import Tabs, { TabOption } from '../Tabs';
import ConnectWalletBtn from '../ConnectWalletBtn';
import TraderCustomScriptTab from './TraderCustomScriptTab';
import { useEVMContext } from '../../context/EVMContext';
import { ethers } from 'ethers';
import { parseContractEvents, ss58ToPublicKey } from '../../utils';


const TraderStakingForm: React.FC<{
  refreshFunc: () => void;
  defaultSubnetId?: string;
}> = ({ refreshFunc, defaultSubnetId = '' }) => {
  const [subnetId, setSubnetId] = useState(defaultSubnetId || '');
  const [stakeAmount, setStakeAmount] = useState('');
  const { setIsLoading, activeAccount } = useAppContext();
  const { accounts, poolContract, signer } = useEVMContext();
  const [activeTab, setActiveTab] = useState<'manual' | 'auto' | 'custom'>('manual');

  const tabs: TabOption[] = [
    { key: 'manual', label: 'Manual' },
    { key: 'auto', label: 'Auto' },
    { key: 'custom', label: 'Custom' }
  ];

  const handleStake = async () => {
    if (!accounts?.[0]) {
      toast.error('Trader ID is not defined');
      return;
    }
    if (!poolContract || !signer) {
      toast.error('EVM Wallet is not connected');
      return;
    }
    setIsLoading(true);
    const connectedWalletAddress = await signer?.getAddress();

    try {
      poolContract.stakeToSubnet(
        Number(subnetId),
        ethers.parseEther(stakeAmount.toString()),
        // '0xbaf1b6c3dc30d7e00d2f74e5443e934f8a026038e03063718786b2b71efe765e'
        ss58ToPublicKey(SN_VAL_ADDRS[Number(subnetId)])
      ).then((tx: any) => {
        return tx.wait();
      }).then(async (receipt: any) => {
        // Parse event from logs
        parseContractEvents(receipt, poolContract);
        toast.success('Stake successful!');

        await apiServices.pool.refresh(connectedWalletAddress, false);
        refreshFunc();
      }).catch((error: any) => {
        console.error('Error while deposits:', error);
        toast.error(`Failed to deposit: ${error.message || 'Please try again.'}`);
      });
    } catch (error) {
      console.log(`Failed to stake:`, error);
      toast.error(`Failed to stake. Please try again.`);
    }
    setIsLoading(false);
  };

  const handleUnstake = async () => {
    if (!accounts[0]) {
      toast.error('Error: Trader ID is not defined');
      return;
    }
    if (!poolContract || !signer) {
      toast.error('EVM Wallet is not connected');
      return;
    }
    setIsLoading(true);
    const connectedWalletAddress = await signer?.getAddress();

    try {
      poolContract.unstakeAllFromSubnet(
        Number(subnetId),
      ).then((tx: any) => {
        return tx.wait();
      }).then(async (receipt: any) => {
        // Parse event from logs
        parseContractEvents(receipt, poolContract);
        toast.success('Unstake successful!');

        await apiServices.pool.refresh(connectedWalletAddress, false);
        refreshFunc();
      }).catch((error: any) => {
        console.error('Error while deposits:', error);
        toast.error(`Failed to deposit: ${error.message || 'Please try again.'}`);
      });
    } catch (error) {
      console.log(`Failed to stake:`, error);
      toast.error(`Failed to stake. Please try again.`);
    }
    setIsLoading(false);
  };

  const renderManualStaking = () => (
    <div className='flex gap-[12px] items-center'>
      <StakeInfoForm subnetId={subnetId} setSubnetId={setSubnetId} stakeAmount={stakeAmount} setStakeAmount={setStakeAmount} />
      {accounts[0] ? (
        <div className="flex justify-end gap-4">
          <button
            onClick={handleStake}
            disabled={!subnetId || !stakeAmount || Number(stakeAmount) <= 0}
            className="py-2 bg-[#00FFEE] text-white dark:text-black rounded-[100px] hover:opacity-80 transition-all duration-200 text-[14px] h-[46px] px-[42px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Stake
          </button>
          <button
            onClick={handleUnstake}
            disabled={!subnetId || !stakeAmount || Number(stakeAmount) <= 0}
            className="py-2 bg-gray-100 hover:bg-gray-300 dark:bg-gray-800 text-black dark:text-white rounded-[100px] dark:hover:bg-gray-500 transition-all duration-200 text-[14px] h-[46px] px-[42px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Unstake
          </button>
        </div>
      ) : (
        <div className="flex justify-end gap-4">
          <ConnectWalletBtn />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabKey) => setActiveTab(tabKey as 'manual' | 'auto' | 'custom')}
      />

      <GradientBorderBox
        title="Staking | Unstaking"
        className="p-6"
      >
        {/* Tab Content */}
        <div className="mb-6">
          {activeTab === 'manual' && renderManualStaking()}
          {activeTab === 'auto' && <TraderAutoStakingTab subnetId={subnetId} stakeAmount={stakeAmount} setSubnetId={setSubnetId} setStakeAmount={setStakeAmount} />}
          {activeTab === 'custom' && <TraderCustomScriptTab refreshFunc={refreshFunc} />}
        </div>
      </GradientBorderBox>
    </div>
  );
};

export default TraderStakingForm;